import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../utils/helpers';
import AddClaimDialog from '../components/AddClaimDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Edit, Trash2, StickyNote, Upload, FileText, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusTone = (s) => {
  if (s === 'Settled') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (s === 'Rejected') return 'bg-red-100 text-red-800 border-red-200';
  if (s === 'Closed') return 'bg-slate-100 text-slate-700 border-slate-200';
  if (s === 'Query raised') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-blue-100 text-blue-800 border-blue-200';
};

const ClaimDetail = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [notes, setNotes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, n, d] = await Promise.all([
        axios.get(`${API}/claims/${claimId}`, { withCredentials: true }),
        axios.get(`${API}/claims/${claimId}/notes`, { withCredentials: true }),
        axios.get(`${API}/claims/${claimId}/documents`, { withCredentials: true }),
      ]);
      setClaim(c.data);
      setNotes(n.data || []);
      setDocuments(d.data || []);
    } catch (e) {
      toast.error('Failed to load claim');
    } finally { setLoading(false); }
  }, [claimId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!noteBody.trim()) { toast.error('Note is empty'); return; }
    setSavingNote(true);
    try {
      await axios.post(`${API}/claims/${claimId}/notes`, { note: noteBody.trim() }, { withCredentials: true });
      setNoteBody('');
      toast.success('Note added');
      const { data } = await axios.get(`${API}/claims/${claimId}/notes`, { withCredentials: true });
      setNotes(data || []);
    } catch (e) {
      toast.error('Failed to add note');
    } finally { setSavingNote(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/claims/${claimId}/documents`, fd, { withCredentials: true });
      toast.success('Document uploaded');
      const { data } = await axios.get(`${API}/claims/${claimId}/documents`, { withCredentials: true });
      setDocuments(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await axios.delete(`${API}/claims/${claimId}/documents/${docId}`, { withCredentials: true });
      setDocuments((d) => d.filter((x) => x.claim_doc_id !== docId));
    } catch (e) { toast.error('Delete failed'); }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/claims/${claimId}`, { withCredentials: true });
      toast.success('Claim deleted');
      navigate('/claims');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;
  if (!claim) return <div className="p-6 text-slate-600">Claim not found.</div>;

  return (
    <div className="space-y-6" data-testid="claim-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/claims')} data-testid="claim-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>{claim.claim_number}</h1>
            <p className="text-slate-600 text-sm">{claim.client_name} · {claim.claim_type}</p>
          </div>
          <Badge variant="outline" className={`text-xs ${statusTone(claim.status)}`}>{claim.status}</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditing(true)} data-testid="claim-edit-button">
            <Edit className="h-4 w-4 mr-1.5" /> Edit
          </Button>
          <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleting(true)} data-testid="claim-delete-button">
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left — detail panel */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Claim Details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><p className="text-[11px] uppercase text-slate-500">Client</p><p className="font-medium">{claim.client_name}</p></div>
              <div><p className="text-[11px] uppercase text-slate-500">Policy</p><p>{claim.policy_number ? `${claim.policy_number} · ${claim.insurer || ''}` : '— no linked policy —'}</p></div>
              <div><p className="text-[11px] uppercase text-slate-500">Insurer Ref #</p><p>{claim.insurer_claim_ref || '—'}</p></div>
              <div><p className="text-[11px] uppercase text-slate-500">Claimed Amount</p><p className="font-medium">{claim.claimed_amount ? formatCurrency(claim.claimed_amount) : '—'}</p></div>
              <div><p className="text-[11px] uppercase text-slate-500">Date of Loss</p><p>{claim.date_of_loss ? formatDate(claim.date_of_loss) : '—'}</p></div>
              <div><p className="text-[11px] uppercase text-slate-500">Date Reported</p><p>{claim.date_reported ? formatDate(claim.date_reported) : '—'}</p></div>
              <div><p className="text-[11px] uppercase text-slate-500">Next Follow-up</p><p>{claim.next_followup_date ? formatDate(claim.next_followup_date) : '—'}</p></div>
              <div><p className="text-[11px] uppercase text-slate-500">Created</p><p>{claim.created_at ? formatDate(claim.created_at) : '—'}</p></div>
              {claim.status === 'Settled' && (
                <>
                  <div><p className="text-[11px] uppercase text-slate-500">Settled Amount</p><p className="font-semibold text-emerald-700">{claim.settled_amount ? formatCurrency(claim.settled_amount) : '—'}</p></div>
                  <div><p className="text-[11px] uppercase text-slate-500">Settled Date</p><p>{claim.settled_date ? formatDate(claim.settled_date) : '—'}</p></div>
                </>
              )}
              {claim.status === 'Rejected' && (
                <div className="md:col-span-2"><p className="text-[11px] uppercase text-slate-500">Rejection Reason</p><p className="text-red-700">{claim.rejection_reason || '—'}</p></div>
              )}
              {claim.description && (
                <div className="md:col-span-2"><p className="text-[11px] uppercase text-slate-500">Description</p><p>{claim.description}</p></div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <StickyNote className="h-4 w-4 text-amber-700" /> Follow-up Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Add a dated note…" data-testid="claim-note-input" />
                <Button onClick={addNote} disabled={savingNote} data-testid="claim-note-save">
                  {savingNote ? 'Saving…' : 'Add'}
                </Button>
              </div>
              {notes.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No notes yet.</p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="claim-notes-list">
                  {notes.map((n) => (
                    <li key={n.note_id} className="py-2">
                      <p className="text-sm text-slate-800">{n.note}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{formatDate(n.created_at)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right — documents */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <FileText className="h-4 w-4 text-blue-700" /> Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Label className="text-xs">Attach a document (PDF, JPG, PNG, WEBP · max 10 MB)</Label>
              <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onUpload} data-testid="claim-doc-input" />
              {uploading && <p className="text-xs text-slate-500">Uploading…</p>}
              {documents.length === 0 ? (
                <p className="text-sm text-slate-500 py-2">No documents attached.</p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="claim-docs-list">
                  {documents.map((d) => (
                    <li key={d.claim_doc_id} className="py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{d.original_filename}</p>
                        <p className="text-[11px] text-slate-500">{formatDate(d.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a href={`${API}/claim-docs/${d.claim_doc_id}/download`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-slate-100 text-slate-700" data-testid={`claim-doc-download-${d.claim_doc_id}`}>
                          <Download className="h-3.5 w-3.5" />
                        </a>
                        <button type="button" onClick={() => deleteDoc(d.claim_doc_id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`claim-doc-delete-${d.claim_doc_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <AddClaimDialog
        open={editing}
        onClose={() => setEditing(false)}
        onSaved={() => load()}
        editing={claim}
      />

      <AlertDialog open={deleting} onOpenChange={setDeleting}>
        <AlertDialogContent data-testid="claim-detail-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete claim {claim.claim_number} permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the claim, its {notes.length} note{notes.length === 1 ? '' : 's'}, and all attached documents. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="claim-detail-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClaimDetail;
